import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import api from '../services/api';
import { setDemoModeForApi } from '../services/api';
import { useCurrency } from './CurrencyContext';

const DemoContext = createContext(null);

export function DemoProvider({ children }) {
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [demoAvailable, setDemoAvailable] = useState(false);
  const [demoUserId, setDemoUserId] = useState(null);
  const [demoDisplayName, setDemoDisplayName] = useState('Demo Portfolio');
  const { refreshCurrency } = useCurrency();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  // Check if demo portfolio is available when user is authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setDemoAvailable(false);
      setDemoUserId(null);
      return;
    }
    const checkDemo = async () => {
      try {
        const res = await api.get('/users/demo-info');
        if (res.data?.success && res.data?.data?.available) {
          setDemoAvailable(true);
          setDemoUserId(res.data.data.demoUserId);
          setDemoDisplayName(res.data.data.demoDisplayName || 'Demo Portfolio');
        }
      } catch (err) {
        // Demo not available, silently fail
      }
    };
    checkDemo();
  }, [isAuthenticated]);

  // Sync demo mode state with the API layer
  useEffect(() => {
    setDemoModeForApi(isDemoMode);
  }, [isDemoMode]);

  // Refresh currency when demo mode actually CHANGES — NOT on mount. The mount-time
  // call raced CurrencyContext's initial preference load and spuriously bumped
  // currencyVersion, which double-fired every dashboard data fetch (the intermittent
  // double-fetch). The preference is already loaded on auth, so the mount refresh was
  // redundant. A mount-ref guard skips the first run; genuine demo toggles still refresh.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) { didMountRef.current = true; return; }
    // Platform audit (C1): force — the dataset behind every page changed, so
    // currencyVersion must bump (and watchers refetch) even when the demo
    // user's currency happens to equal the real user's.
    refreshCurrency({ force: true });
  }, [isDemoMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const enterDemoMode = useCallback(() => {
    setIsDemoMode(true);
  }, []);

  const exitDemoMode = useCallback(() => {
    setIsDemoMode(false);
  }, []);

  const value = {
    isDemoMode,
    demoAvailable,
    demoUserId,
    demoDisplayName,
    enterDemoMode,
    exitDemoMode
  };

  return (
    <DemoContext.Provider value={value}>
      {children}
    </DemoContext.Provider>
  );
}

export function useDemo() {
  const context = useContext(DemoContext);
  if (context === null) {
    throw new Error('useDemo must be used within a DemoProvider');
  }
  return context;
}

export default DemoContext;
